import { useFormat, useI18n } from '@/i18n'
import { Badge, Banner, Card, CardBody, CardHeader } from '@/ui'
import { liveOutputs, type ProcessState, type RunView } from '../../projection'
import { outputName, problemKey } from '../../components/labels'

/** After close: what the server said (or that it has not heard yet), the sacks, the samples. */
export function ClosedSection({ state, run }: { state: ProcessState; run: RunView }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const byKey = new Map<string, { n: number; g: number; stream: 'MAIN' | 'CONV' }>()
  for (const o of liveOutputs(run)) {
    const x = byKey.get(o.stream_key) ?? { n: 0, g: 0, stream: o.stream }
    x.n += 1
    x.g += o.net_g
    byKey.set(o.stream_key, x)
  }

  return (
    <div className="flex flex-col gap-4">
      {run.status === 'CANCELLED' && <Banner tone="info">{t('process.closed.cancelled')}</Banner>}
      {run.status === 'CLOSED' && (
        <Banner tone="info" title={t('process.closed.waitingTitle')}>
          {t('process.closed.waitingBody')}
        </Banner>
      )}
      {run.status === 'BALANCED' && run.open_exceptions.length === 0 && (
        <Banner tone="success" title={t('process.closed.balancedTitle')}>
          {t('process.closed.balancedBody')}
        </Banner>
      )}
      {(run.status === 'EXCEPTION' || run.open_exceptions.length > 0) && (
        <Banner tone="warn" title={t('process.closed.heldTitle')}>
          {t('process.closed.heldBody')}
          {run.open_exceptions.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-1">
              {run.open_exceptions.map((e) => (
                <Badge key={e.id} tone="warn">
                  {t(problemKey(e.type))}
                </Badge>
              ))}
            </span>
          )}
        </Banner>
      )}
      {byKey.size > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.closed.outputs')} />
          <CardBody className="py-1">
            <ul>
              {[...byKey.entries()].map(([k, x]) => (
                <li key={k} className="flex items-baseline justify-between border-b border-rule/70 py-2 last:border-b-0">
                  <span className="flex items-center gap-2">
                    {outputName(state, run.step_code, k, locale, t)}
                    {x.stream === 'CONV' && <Badge tone="danger">{t('process.tie.redShort')}</Badge>}
                  </span>
                  <span className="font-mono text-[14px] tabular">
                    {t('process.sacksN', { n: x.n })} · {fmt.weight(x.g)}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      {run.samples.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.closed.samples')} />
          <CardBody className="py-1">
            <ul>
              {run.samples.map((s) => (
                <li key={s.sample_id} className="flex items-baseline justify-between py-2">
                  <span className="font-mono">{s.seal_payload}</span>
                  <Badge tone="ok">{t(s.purpose === 'CONFIRMING' ? 'process.qc.confirming' : 'process.qc.sealed')}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
